import { Header } from '../common/Header';
import Card from '@mui/material/Card';
import TableGrid from '../table-grid/TableGrid';
import { EndPoints } from '../../axios/endPoints';
import { GridColDef } from '@mui/x-data-grid';
import { Button, Grid, IconButton, Tooltip } from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import { handleToggleFileDetailsPopup, handleToggleTrainingModelDetailsDialog } from '../../store/mlcreator-slice/mlcreator';
import TrainingModelDetails from '../ml-creator/TrainingModelDetails';
import { handleNewPrediction, handleRefreshSelectedMLTesterTable, handleToggleDeletePredictionResult, handleTogglePredictionResultDetailsDialog } from '../../store/mltester-slice/mltester';
import NewPredictionDialog from './NewPredictionDialog';
import SuccessfulPredictionDetails from './SuccessfulPredictionDetails';
import CloudDownloadTwoToneIcon from '@mui/icons-material/CloudDownloadTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import { DownloadFile } from '../../services/FileServices';
import DeletePredictionResultDialog from './DeletePredictionResultDialog';
import PredictionResultDetailsDialog from './PredictionResultDetailsDialog';
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import RefreshIcon from '@mui/icons-material/Refresh';

interface MLTesterProps {};

const Old_SelectedMLTester = React.forwardRef<HTMLDivElement, MLTesterProps>((props, ref) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const selectedTesterRow = useAppSelector((state: RootState) => state.mltester.selectedTesterRow);
    const refreshSelectedMLTesterTable = useAppSelector((state: RootState) => state.mltester.refreshSelectedMLTesterTable);

    const memoizedSelectedTesterRow = React.useMemo(() => selectedTesterRow, [selectedTesterRow]);

    const selecetdMLTesterColumns: GridColDef[] = useMemo(() => [
        { field: 'id', headerName: t('SELECTED_TESTER_ID_FIELD') },
        { field: 'name', headerName: t('SELECTED_TESTER_RESULT_NAME_FIELD'), minWidth: 225 },
        { field: 'trainedModelName', headerName: t('SELECTED_TESTER_MODEL_NAME_FIELD'), minWidth: 225, 
            renderCell: (params) => {
                return(<div className="styled-div" onClick={() => dispatch(handleToggleTrainingModelDetailsDialog({ 
                    isOpen: true,
                    body: { 
                        id: params.row.trainedModelId,
                        name: params.row.trainedModelName
                    }
                }))}>{params.value}</div>)
            }
        },
        { field: 'trainedModelSelectedTypeString', headerName: t('SELECTED_TESTER_MODEL_TYPE_FIELD'), minWidth: 150, renderCell: (params) => { return t(`${params.value}`)} },
        { field: 'trainedModelSelectedName', headerName: t('SELECTED_TESTER_MODEL_SUBTYPE_FIELD'), minWidth: 200 },
        { field: 'trainedModelTrainingDatasetName', headerName: t('SELECTED_TESTER_TRAINING_DATASET_NAME_FIELD'), minWidth: 225, 
            renderCell: (params) => {
                return(<div className="styled-div" onClick={() => {
                    dispatch(handleToggleFileDetailsPopup({
                        open: true,
                        body: {
                            fileId: params.row.trainedModelTrainingDatasetId,
                            fileName: params.row.trainedModelTrainingDatasetName
                        }
                    }))
                }}>{params.value}</div>)
            } 
        },
        { field: 'testingDate', headerName: t('SELECTED_TESTER_TESTING_DATE_FIELD'), minWidth: 175, valueFormatter: (params) => moment.utc(params.value).local().format('DD/MM/YYYY HH:mm') },
        { field: 'actions', headerName: t('TESTER_ACTIONS_FIELD'), minWidth: 300, renderCell: (params) => {
            return(
                <Grid container spacing={3}>
                    <Grid item>
                        <Tooltip title={t('TESTER_DETAILS_ACTION')}>
                            <IconButton onClick={() => dispatch(handleTogglePredictionResultDetailsDialog({ isOpen: true, body: { id: params.row.id, name: params.row.name }}))}>
                                <InfoTwoToneIcon className="info-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Tooltip title={t('TESTER_DOWNLOAD_ACTION')}>
                            <IconButton onClick={() => {
                                DownloadFile(EndPoints.ML_TESTER_DOWNLOAD_PREDICTION_RESULT + `?id=${params.row.id}`)
                            }}>
                                <CloudDownloadTwoToneIcon className="download-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Tooltip title={t('TESTER_DELETE_ACTION')}>
                            <IconButton onClick={() => dispatch(handleToggleDeletePredictionResult({ isOpen: true, body: { id: params.row.id, name: params.row.name }}))}>
                                <DeleteForeverTwoToneIcon className="delete-icon" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            )
        }}
    ], [dispatch, t])
    
    return(
        <React.Fragment>
            { memoizedSelectedTesterRow ?
            <Card style={{marginTop: '5px'}} ref={ref}>
                <Header 
                    title={t('SELECTED_TESTER_ROW', { name: memoizedSelectedTesterRow.name })} 
                    helperIcon={
                        <RedirectIconButton to={'/help/6'} className="helper-icon" >
                            <ContactSupportIcon />
                        </RedirectIconButton>
                    }
                />
                <Grid container p={2} spacing={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Grid item display="flex" justifyContent="space-between">
                        <Button 
                            onClick={() => { dispatch(handleNewPrediction({ isOpen: true })); }} 
                            variant='contained' 
                            className='button-color'
                            size="small"
                        >
                            {t('ML_TESTER_NEW_PREDICTION_BUTTON_TEXT')}
                        </Button>
                        <Tooltip title={t('REFRESH_TABLE_BUTTON_TOOLTIP')}>
                            <IconButton onClick={() => dispatch(handleRefreshSelectedMLTesterTable())}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item sx={{ width: '100%', overflowY: 'auto' }}>
                        <TableGrid 
                            baseURL={EndPoints.ML_TESTER_GET_PREDICTION_RESULTS}
                            aditionalQueryParams={{
                                datasetId: memoizedSelectedTesterRow.id
                            }}
                            columns={selecetdMLTesterColumns}
                            sortColumns={[{ field: 'id', sort: 'asc' }]}
                            refresh={refreshSelectedMLTesterTable}
                            disableRowSelection
                            tableHeight={500}
                            rowHeight={35}
                            columnHeaderHeight={40}
                        />
                    </Grid>
                </Grid>
                <NewPredictionDialog />
                <TrainingModelDetails />
                <SuccessfulPredictionDetails />
                <DeletePredictionResultDialog />
                <PredictionResultDetailsDialog />
            </Card>
            : '' }
        </React.Fragment>
    )
});

export default React.memo(Old_SelectedMLTester)